import { makeStyles } from '@material-ui/core';
import { Close, CloudUpload } from '@mui/icons-material';
import {
    Badge,
    Box,
    CircularProgress,
    FormHelperText,
    IconButton,
    Paper,
    Stack,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { default as React, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import SortableList, { SortableItem } from 'react-easy-sort';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import Lightbox from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { Media } from '../types/user';
import toast from './Toast';
import { Card, CardMedia } from '@mui/material';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

const useStyles = makeStyles({
    list: {
        display: 'flex',
        flexWrap: 'wrap',
        userSelect: 'none',
        gap: 8,
    },

    item: {
        cursor: 'grab',
        userSelect: 'none',
    },

    dragged: {},
});

export type FileOrientation = 'landscape' | 'portrait' | 'any';

export interface FileResolution {
    width: number;
    height: number;
}

export interface FileValidationConfig {
    acceptedTypes?: { [key: string]: string[] };
    maxFileSize?: number; // in bytes
    maxFiles?: number;
    orientation?: FileOrientation;
    resolution?: FileResolution;
    customValidation?: (
        file: File,
    ) => Promise<{ valid: boolean; message?: string }>;
}

interface FormUploadProps {
    name: string;
    control: Control<any>;
    onUpload: (files: File[]) => Promise<Media[] | undefined>;
    disabled?: boolean;
    showFiles?: boolean;
    validation?: FileValidationConfig;
    uploadText?: string;
    helperText?: string;
    mediaType?: string;
    hasLightBox?: boolean;
    /** Shorter, tighter drop zone (e.g. profile summary photo row). */
    compact?: boolean;
    /** Larger drop zone + typography (e.g. managed user profile photo edit). */
    emphasizedDropzone?: boolean;
    /** Optional ref to the hidden file input (e.g. trigger from a Save button). */
    fileInputRef?: React.MutableRefObject<HTMLInputElement | null>;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const FormUpload = ({
    name,
    control,
    onUpload,
    disabled = false,
    showFiles = true,
    validation = {
        acceptedTypes: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
        },
        maxFileSize: 10 * 1024 * 1024, // 10MB default
        orientation: 'landscape',
    },
    uploadText = 'Click to browse files or drag and drop',
    helperText,
    mediaType,
    hasLightBox,
    compact = false,
    emphasizedDropzone = false,
    fileInputRef,
    inputProps: inputPropsFromProps,
}: FormUploadProps) => {
    const [lightboxPhotos, setLightboxPhotos] = useState<Media[]>([]);
    const [selectedPhoto, setSelectedPhoto] = useState<number>();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const classes = useStyles();

    const { fields, append, remove, move } = useFieldArray({
        name,
        control,
    });

    const validateFileSize = (file: File, maxSize?: number): boolean => {
        if (!maxSize) return true;
        return file.size <= maxSize;
    };

    const validateFileType = (
        file: File,
        acceptedTypes?: { [key: string]: string[] },
    ): boolean => {
        if (!acceptedTypes) return true;

        for (const [mimeType, extensions] of Object.entries(acceptedTypes)) {
            if (mimeType === '*/*') return true;

            if (mimeType.endsWith('/*')) {
                const baseType = mimeType.split('/')[0];
                if (file.type.startsWith(baseType + '/')) return true;
            } else if (file.type === mimeType) {
                return true;
            }

            const fileExtension =
                '.' + file.name.split('.').pop()?.toLowerCase();
            if (extensions.includes(fileExtension)) return true;
        }
        return false;
    };

    const validateImageOrientation = (
        file: File,
        requiredOrientation?: FileOrientation,
    ): Promise<boolean> => {
        if (!requiredOrientation || requiredOrientation === 'any') {
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            const img = new Image();
            img.onload = function () {
                const height = img.height;
                const width = img.width;
                const isLandscape = width > height;

                if (requiredOrientation === 'landscape') {
                    resolve(isLandscape);
                } else if (requiredOrientation === 'portrait') {
                    resolve(!isLandscape);
                } else {
                    resolve(true);
                }
            };
            img.onerror = () => resolve(false);
            img.src = URL.createObjectURL(file);
        });
    };

    const validateImageResolution = (
        file: File,
        requiredResolution?: FileResolution,
    ): Promise<boolean> => {
        if (!requiredResolution) return Promise.resolve(true);

        const url = URL.createObjectURL(file);

        return new Promise((resolve) => {
            const img = new Image();

            img.onload = () => {
                URL.revokeObjectURL(url);
                resolve(
                    img.height >= requiredResolution.height &&
                        img.width >= requiredResolution.width,
                );
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(false);
            };

            img.src = url;
        });
    };

    const validateVideoResolution = (
        file: File,
        requiredResolution?: FileResolution,
    ): Promise<boolean> => {
        if (!requiredResolution) return Promise.resolve(true);

        const url = URL.createObjectURL(file);

        return new Promise((resolve) => {
            const video = document.createElement('video');

            video.onloadedmetadata = () => {
                URL.revokeObjectURL(url);
                resolve(
                    video.videoHeight >= requiredResolution.height &&
                        video.videoWidth >= requiredResolution.width,
                );
            };

            video.onerror = () => {
                URL.revokeObjectURL(url);
                resolve(false);
            };

            video.preload = 'metadata';
            video.src = url;
        });
    };

    const validateVideoOrientation = (
        file: File,
        requiredOrientation?: FileOrientation,
    ): Promise<boolean> => {
        if (!requiredOrientation || requiredOrientation === 'any') {
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';

            video.onloadedmetadata = function () {
                const height = video.videoHeight;
                const width = video.videoWidth;
                const isLandscape = width > height;

                if (requiredOrientation === 'landscape') {
                    resolve(isLandscape);
                } else if (requiredOrientation === 'portrait') {
                    resolve(!isLandscape);
                } else {
                    resolve(true);
                }
            };

            video.onerror = () => resolve(false);
            video.src = URL.createObjectURL(file);
        });
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const generateAcceptedTypesText = (acceptedTypes?: {
        [key: string]: string[];
    }): string => {
        if (!acceptedTypes) return 'All file types';

        const extensions: string[] = [];
        for (const [mimeType, exts] of Object.entries(acceptedTypes)) {
            if (mimeType === '*/*') return 'All file types';
            extensions.push(...exts);
        }

        return extensions
            .map((ext) => ext.toUpperCase().replace('.', ''))
            .join(', ');
    };

    const onDrop = async (files: File[]) => {
        const validFiles: File[] = [];
        const rejectedFiles: { file: File; reason: string }[] = [];

        // Check if adding these files would exceed the maximum limit
        if (
            validation.maxFiles &&
            fields.length + files.length > validation.maxFiles
        ) {
            const availableSlots = validation.maxFiles - fields.length;
            toast.error(
                `Maximum ${validation.maxFiles} files allowed. You can only add ${availableSlots} more file(s).`,
            );
            return;
        }

        for (const file of files) {
            let isValid = true;
            let rejectionReason = '';

            if (!validateFileType(file, validation.acceptedTypes)) {
                isValid = false;
                rejectionReason = `Invalid file type. Supported: ${generateAcceptedTypesText(
                    validation.acceptedTypes,
                )}`;
            }

            if (isValid && !validateFileSize(file, validation.maxFileSize)) {
                isValid = false;
                rejectionReason = `File too large. Maximum size: ${formatFileSize(
                    validation.maxFileSize || 0,
                )}`;
            }

            if (isValid) {
                validFiles.push(file);
            } else {
                rejectedFiles.push({ file, reason: rejectionReason });
            }
        }

        if (rejectedFiles.length > 0) {
            const uniqueReasons = Array.from(
                new Set(rejectedFiles.map((rf) => rf.reason)),
            );
            uniqueReasons.forEach((reason) => {
                const count = rejectedFiles.filter(
                    (rf) => rf.reason === reason,
                ).length;
                toast.error(`${count} file(s) rejected: ${reason}`);
            });
        }

        if (validFiles.length === 0) {
            return;
        }

        const orientationValidationResults = await Promise.all(
            validFiles.map(async (file) => {
                if (
                    !validation.orientation ||
                    validation.orientation === 'any'
                ) {
                    return { file, valid: true };
                }

                const isImage = file.type.startsWith('image/');
                const isVideo = file.type.startsWith('video/');

                if (!isImage && !isVideo) {
                    return { file, valid: true };
                }

                let orientationValid: boolean;
                if (isImage) {
                    orientationValid = await validateImageOrientation(
                        file,
                        validation.orientation,
                    );
                } else {
                    orientationValid = await validateVideoOrientation(
                        file,
                        validation.orientation,
                    );
                }

                return { file, valid: orientationValid };
            }),
        );

        const orientationValidFiles = orientationValidationResults
            .filter((result) => result.valid)
            .map((result) => result.file);

        const orientationRejectedFiles = orientationValidationResults.filter(
            (result) => !result.valid,
        );

        if (orientationRejectedFiles.length > 0) {
            const orientationText =
                validation.orientation === 'landscape'
                    ? 'landscape'
                    : 'portrait';
            toast.error(
                `${orientationRejectedFiles.length} file(s) rejected. Files must be ${orientationText} orientation.`,
            );
        }

        if (orientationValidFiles.length === 0) {
            return;
        }

        const resolutionValidationResults = await Promise.all(
            orientationValidFiles.map(async (file) => {
                if (!validation.resolution) {
                    return { file, valid: true };
                }

                const isImage = file.type.startsWith('image/');
                const isVideo = file.type.startsWith('video/');

                if (!isImage && !isVideo) {
                    return { file, valid: true };
                }

                let resolutionValid: boolean;
                if (isImage) {
                    resolutionValid = await validateImageResolution(
                        file,
                        validation.resolution,
                    );
                } else {
                    resolutionValid = await validateVideoResolution(
                        file,
                        validation.resolution,
                    );
                }

                return { file, valid: resolutionValid };
            }),
        );

        const finalValidFiles = resolutionValidationResults
            .filter((result) => result.valid)
            .map((result) => result.file);

        const resolutionRejectedFiles = resolutionValidationResults.filter(
            (result) => !result.valid,
        );

        if (resolutionRejectedFiles.length > 0) {
            toast.error(
                `${resolutionRejectedFiles.length} file(s) rejected. Files must be minimum ${validation.resolution?.width} x ${validation.resolution?.height} resolution.`,
            );
        }

        if (finalValidFiles.length === 0) {
            return;
        }

        if (validation.customValidation) {
            const customValidationResults = await Promise.all(
                finalValidFiles.map(async (file) => {
                    const result = await validation.customValidation!(file);
                    return { file, result };
                }),
            );

            const customValidFiles = customValidationResults
                .filter(({ result }) => result.valid)
                .map(({ file }) => file);

            const customRejectedFiles = customValidationResults.filter(
                ({ result }) => !result.valid,
            );

            if (customRejectedFiles.length > 0) {
                customRejectedFiles.forEach(({ result }) => {
                    if (result.message) {
                        toast.error(result.message);
                    }
                });
            }

            if (customValidFiles.length === 0) {
                return;
            }

            setUploading(true);
            const uploads = await onUpload(customValidFiles);
            setUploading(false);
            if (!uploads) return;
            append(uploads);
        } else {
            setUploading(true);
            const uploads = await onUpload(finalValidFiles);
            setUploading(false);
            if (!uploads) return;
            append(uploads);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: validation.acceptedTypes || { '*/*': [] },
        maxSize: validation.maxFileSize,
        maxFiles: validation.maxFiles,
        onDropRejected: (fileRejections) => {
            const typeRejections = fileRejections.filter((rejection) =>
                rejection.errors.some(
                    (error) => error.code === 'file-invalid-type',
                ),
            );

            const sizeRejections = fileRejections.filter((rejection) =>
                rejection.errors.some(
                    (error) => error.code === 'file-too-large',
                ),
            );

            const countRejections = fileRejections.filter((rejection) =>
                rejection.errors.some(
                    (error) => error.code === 'too-many-files',
                ),
            );

            if (typeRejections.length > 0) {
                toast.error(
                    `Unsupported file format. Supported: ${generateAcceptedTypesText(
                        validation.acceptedTypes,
                    )}`,
                );
            }

            if (sizeRejections.length > 0) {
                toast.error(
                    `File(s) too large. Maximum size: ${formatFileSize(
                        validation.maxFileSize || 0,
                    )}`,
                );
            }

            if (countRejections.length > 0) {
                toast.error(
                    `Too many files. Maximum: ${
                        validation.maxFiles || 'unlimited'
                    }`,
                );
            }
        },
    });

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        move(oldIndex, newIndex);
    };
    const handlePhotoClick = (index: number, photos: Media[]) => {
        if (hasLightBox) {
            setLightboxPhotos(photos);
            setSelectedPhoto(index);
            setLightboxOpen(true);
        }
    };

    return (
        <>
            {showFiles && fields.length > 0 && (
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        overflowX: 'auto',
                        pb: 1,
                        mb: 2,
                        pt: 1,
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            borderRadius: 3,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: 3,
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)',
                            },
                        },
                    }}
                >
                    <SortableList
                        onSortEnd={onSortEnd}
                        className={classes.list}
                        draggedItemClassName={classes.dragged}
                    >
                        {fields?.map((field, index) => (
                            <SortableItem key={field.id}>
                                <Badge
                                    key={field.id}
                                    overlap='circular'
                                    invisible={disabled}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    badgeContent={
                                        <IconButton
                                            sx={{
                                                p: 0.3,
                                                backgroundColor:
                                                    'custom.lighterGrey',
                                                color: 'text.primary',
                                                '&:hover': {
                                                    backgroundColor:
                                                        'custom.grey',
                                                },
                                                boxShadow: 2,
                                                fontSize: '12px',
                                                transform: 'translateX(2px)',
                                            }}
                                            onClick={() => remove(index)}
                                        >
                                            <Close sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    }
                                >
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            p: 0.5,
                                            overflow: 'hidden',
                                        }}
                                        onClick={() =>
                                            handlePhotoClick(
                                                index,
                                                fields as Media[],
                                            )
                                        }
                                    >
                                        <Card
                                            className={classes.item}
                                            sx={{
                                                width: '150px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 1,
                                            }}
                                        >
                                            <CardMedia
                                                component={
                                                    mediaType ?? ('img' as any)
                                                }
                                                src={(field as Media).src}
                                                controls={true}
                                                height='100px'
                                                sx={{
                                                    width: '100%',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Card>
                                    </Paper>
                                </Badge>
                            </SortableItem>
                        ))}
                    </SortableList>
                </Stack>
            )}
            <Controller
                name={name}
                control={control}
                render={({ fieldState: { error } }) => {
                    const emph = Boolean(compact && emphasizedDropzone);
                    const inputProps = !disabled ? getInputProps() : {};
                    const { ref: dzInputRef, ...inputPropsRest } =
                        inputProps as typeof inputProps & {
                            ref?:
                                | React.RefCallback<HTMLInputElement>
                                | React.RefObject<HTMLInputElement | null>
                                | null;
                        };

                    return (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    minHeight: emph
                                        ? 132
                                        : compact
                                          ? 76
                                          : isMobile
                                            ? 140
                                            : isTablet
                                              ? 160
                                              : 180,
                                    py: emph
                                        ? 2.5
                                        : compact
                                          ? 1
                                          : isMobile
                                            ? 2
                                            : 3,
                                    border: isDragActive
                                        ? '2px dashed'
                                        : '2px dashed',
                                    borderColor: isDragActive
                                        ? 'primary.main'
                                        : alpha(theme.palette.grey[600], 0.5),
                                    borderRadius: 3,
                                    backgroundColor: isDragActive
                                        ? alpha(
                                              theme.palette.primary.main,
                                              0.05,
                                          )
                                        : alpha(theme.palette.grey[50], 0.8),
                                    cursor: disabled
                                        ? 'not-allowed'
                                        : 'pointer',
                                    transition: 'all 0.2s ease-in-out',
                                    opacity: disabled ? 0.6 : 1,
                                    '&:hover': !disabled
                                        ? {
                                              borderColor: 'primary.main',
                                              backgroundColor: alpha(
                                                  theme.palette.primary.main,
                                                  0.04,
                                              ),
                                          }
                                        : {},
                                }}
                                {...(!disabled ? getRootProps() : {})}
                            >
                                <input
                                    {...inputPropsRest}
                                    {...inputPropsFromProps}
                                    ref={(node) => {
                                        const r = dzInputRef as
                                            | React.RefCallback<HTMLInputElement>
                                            | React.RefObject<HTMLInputElement | null>
                                            | null
                                            | undefined;
                                        if (typeof r === 'function') {
                                            r(node);
                                        } else if (
                                            r &&
                                            typeof r === 'object' &&
                                            'current' in r
                                        ) {
                                            (
                                                r as React.MutableRefObject<HTMLInputElement | null>
                                            ).current = node;
                                        }
                                        if (fileInputRef) {
                                            fileInputRef.current = node;
                                        }
                                    }}
                                    multiple
                                    disabled={disabled}
                                    //input has some in-built styles causing double scrollbar in forms
                                    style={{
                                        position: 'absolute',
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        width: 0,
                                        height: 0,
                                        overflow: 'hidden',
                                    }}
                                />
                                <Stack
                                    spacing={
                                        emph
                                            ? 1.25
                                            : compact
                                              ? 0.5
                                              : isMobile
                                                ? 1
                                                : 2
                                    }
                                    sx={{
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        px: emph
                                            ? 2.5
                                            : compact
                                              ? 2
                                              : isMobile
                                                ? 1.5
                                                : 3,
                                        width: '100%',
                                        maxWidth: '100%',
                                        flex: 1, // Allow the stack to grow
                                        justifyContent: 'center',
                                    }}
                                >
                                    {uploading ? (
                                        <>
                                            <CircularProgress
                                                size={
                                                    emph
                                                        ? 36
                                                        : compact
                                                          ? 28
                                                          : isMobile
                                                            ? 36
                                                            : 48
                                                }
                                                thickness={4}
                                            />
                                            <Typography
                                                variant={
                                                    isMobile ? 'body2' : 'body1'
                                                }
                                                color='text.primary'
                                                fontWeight={500}
                                            >
                                                Uploading files...
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Box
                                                sx={{
                                                    p: emph
                                                        ? 1.25
                                                        : compact
                                                          ? 1
                                                          : 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor:
                                                        isDragActive
                                                            ? 'primary.main'
                                                            : alpha(
                                                                  theme.palette
                                                                      .grey[500],
                                                                  0.12,
                                                              ),
                                                    color: isDragActive
                                                        ? 'white'
                                                        : 'primary.main',
                                                    transition:
                                                        'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <CloudUpload
                                                    sx={{
                                                        fontSize: emph
                                                            ? 28
                                                            : compact
                                                              ? 20
                                                              : 24,
                                                    }}
                                                />
                                            </Box>
                                            {isDragActive ? (
                                                <Typography
                                                    variant={'body2'}
                                                    color='primary.main'
                                                    fontWeight={600}
                                                    sx={{
                                                        wordBreak: 'break-word',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Drop the files here
                                                </Typography>
                                            ) : (
                                                <Stack
                                                    sx={{
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                    }}
                                                    spacing={1}
                                                >
                                                    <Typography
                                                        variant={
                                                            emph
                                                                ? 'body1'
                                                                : 'body2'
                                                        }
                                                        color={
                                                            emph
                                                                ? 'primary.dark'
                                                                : 'text.primary'
                                                        }
                                                        fontWeight={700}
                                                        sx={{
                                                            wordBreak:
                                                                'break-word',
                                                            textAlign: 'center',
                                                            px: 1,
                                                        }}
                                                    >
                                                        {uploadText}
                                                    </Typography>
                                                    <FormHelperText
                                                        color='text.secondary'
                                                        sx={{
                                                            textAlign: 'center',
                                                            fontSize: emph
                                                                ? '0.8125rem'
                                                                : '9px',
                                                            wordBreak:
                                                                'break-word',
                                                            fontWeight: 500,
                                                            overflowWrap:
                                                                'break-word',
                                                            hyphens: 'auto',
                                                            px: 1,
                                                            maxWidth: '100%',
                                                            lineHeight: isMobile
                                                                ? 1.2
                                                                : 1.4,
                                                        }}
                                                    >
                                                        {helperText ||
                                                            (() => {
                                                                const parts: string[] =
                                                                    [];

                                                                const acceptedTypesText =
                                                                    generateAcceptedTypesText(
                                                                        validation.acceptedTypes,
                                                                    );
                                                                if (
                                                                    acceptedTypesText !==
                                                                    'All file types'
                                                                ) {
                                                                    parts.push(
                                                                        `Supports ${acceptedTypesText} formats`,
                                                                    );
                                                                }

                                                                if (
                                                                    validation.maxFileSize
                                                                ) {
                                                                    parts.push(
                                                                        `Max size: ${formatFileSize(
                                                                            validation.maxFileSize,
                                                                        )}`,
                                                                    );
                                                                }

                                                                if (
                                                                    validation.maxFiles
                                                                ) {
                                                                    parts.push(
                                                                        `Max files: ${validation.maxFiles}`,
                                                                    );
                                                                }

                                                                if (
                                                                    validation.orientation &&
                                                                    validation.orientation !==
                                                                        'any'
                                                                ) {
                                                                    parts.push(
                                                                        `${validation.orientation} orientation only`,
                                                                    );
                                                                }

                                                                if (
                                                                    validation.resolution
                                                                ) {
                                                                    parts.push(
                                                                        `Min ${validation.resolution.width} x ${validation.resolution.height} resolution`,
                                                                    );
                                                                }

                                                                return parts.length >
                                                                    0
                                                                    ? parts.join(
                                                                          ' • ',
                                                                      )
                                                                    : 'All file types supported';
                                                            })()}
                                                    </FormHelperText>
                                                </Stack>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Box>
                            {!!error && (
                                <FormHelperText
                                    error
                                    sx={{
                                        mt: 1,
                                        textAlign: 'center',
                                        fontWeight: 500,
                                    }}
                                >
                                    {error.message}
                                </FormHelperText>
                            )}
                        </>
                    );
                }}
            />
            <Lightbox
                open={lightboxOpen}
                index={selectedPhoto}
                slides={lightboxPhotos}
                close={() => setLightboxOpen(false)}
                plugins={[Fullscreen, Thumbnails, Zoom]}
            />
        </>
    );
};

export default FormUpload;
